import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { 
  PurchaseRequest, 
  getPurchaseRequestStatusColor,
  paymentTypeOptions,
  paymentCadenceOptions,
  InventoryItem,
  InventoryCategory,
} from "./inventoryTypes";
import { fetchEmployees } from "./inventoryService";
import { DatePicker } from "@/components/ui/date-picker";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Schema for approving a request
const approveRequestSchema = z.object({
  status: z.string().min(1, { message: "Status is required" }),
  actual_cost: z.string().min(1, { message: "Actual cost is required" }),
  payment_type: z.string().min(1, { message: "Payment type is required" }),
  payment_cadence: z.string().optional(),
  payment_amount: z.string().optional(),
  next_payment_date: z.date().optional().nullable(),
  serial: z.string().min(1, { message: "Serial number is required" }),
  location: z.string().min(1, { message: "Location is required" }),
  approved_by: z.number().optional(),
});

interface PurchaseRequestDialogProps {
  request: PurchaseRequest;
  open: boolean;
  onClose: (updated: boolean) => void;
}

export function PurchaseRequestDialog({ request, open, onClose }: PurchaseRequestDialogProps) {
  const [updated, setUpdated] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [noEmployeeRecord, setNoEmployeeRecord] = useState(false);

  // Form for approving or purchasing
  const form = useForm<z.infer<typeof approveRequestSchema>>({
    resolver: zodResolver(approveRequestSchema),
    defaultValues: {
      status: "Approved",
      actual_cost: request.estimated_cost?.toString() || "",
      payment_type: request.payment_type || "One Time",
      payment_cadence: request.payment_cadence || "Monthly",
      payment_amount: request.payment_amount?.toString() || "",
      next_payment_date: request.next_payment_date ? new Date(request.next_payment_date) : null,
      serial: "",
      location: "",
      approved_by: undefined,
    },
  });

  const watchPaymentType = form.watch("payment_type");
  const watchStatus = form.watch("status");

  const { data: employees, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ["employees"],
    queryFn: fetchEmployees,
  });

  // Get the current user's email
  const [userEmail, setUserEmail] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && user.email) {
          setUserEmail(user.email);
        }
      } catch (error) {
        console.error("Failed to fetch current user:", error);
      }
    };
    
    fetchCurrentUser();
  }, []);

  // Find matching employee record for the current user
  useEffect(() => {
    if (userEmail && employees?.length) {
      // Match by checking employee data - need to type check since email might not be accessible directly
      const matchingEmployee = employees.find((emp) => {
        // Check if this employee has the same email as the current user
        return userEmail.toLowerCase() === (emp as any).email?.toLowerCase();
      });
      
      if (matchingEmployee) {
        form.setValue('approved_by', matchingEmployee.id);
        setNoEmployeeRecord(false);
      } else {
        setNoEmployeeRecord(true);
      }
    }
  }, [userEmail, employees, form]);

  const handleApprove = async (data: z.infer<typeof approveRequestSchema>) => {
    if (!data.approved_by) {
      toast({ title: "Error", description: "Approver is required", variant: "destructive" });
      return;
    }
    
    try {
      // First, update the purchase request
      const updateData: Partial<PurchaseRequest> = {
        status: data.status as any,
        approved_by: data.approved_by,
        approved_at: new Date().toISOString(),
        actual_cost: parseFloat(data.actual_cost),
        payment_type: data.payment_type as any,
      };
      
      // Add recursive payment details if applicable
      if (data.payment_type === "Recursive") {
        updateData.payment_cadence = data.payment_cadence as any;
        
        if (data.payment_amount) {
          updateData.payment_amount = parseFloat(data.payment_amount);
        }
        
        if (data.next_payment_date) {
          updateData.next_payment_date = data.next_payment_date.toISOString().split("T")[0];
        }
      }
      
      // Update the purchase request
      const { error: updateError } = await supabase
        .from("purchase_requests")
        .update(updateData)
        .eq("id", request.id);
        
      if (updateError) throw updateError;
      
      // If the status is "Purchased", create an inventory item
      if (data.status === "Purchased") {
        const newItem: Partial<InventoryItem> & { category: InventoryCategory; serial: string } = {
          serial: data.serial,
          description: request.description,
          category: request.category, // Explicitly set category from the request
          cost: parseFloat(data.actual_cost),
          currency: request.currency,
          purchase_date: new Date().toISOString().split("T")[0],
          authorized_by: data.approved_by,
          status: "Available",
          location: data.location,
          payment_type: data.payment_type as any,
        };
        
        // Add recursive payment details if applicable
        if (data.payment_type === "Recursive") {
          newItem.payment_cadence = data.payment_cadence as any;
          
          if (data.payment_amount) {
            newItem.payment_amount = parseFloat(data.payment_amount);
          }
          
          if (data.next_payment_date) {
            newItem.next_payment_date = data.next_payment_date.toISOString().split("T")[0];
          }
        }
        
        // Create the inventory item
        const { data: inventoryItem, error: inventoryError } = await supabase
          .from("inventory_items")
          .insert([newItem])
          .select()
          .single();
          
        if (inventoryError) throw inventoryError;
        
        // Link the inventory item to the purchase request
        if (inventoryItem) {
          const { error: linkError } = await supabase
            .from("purchase_requests")
            .update({ inventory_item_id: inventoryItem.id })
            .eq("id", request.id);
            
          if (linkError) throw linkError;
        }
      }
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["purchaseRequests"] });
      queryClient.invalidateQueries({ queryKey: ["inventoryItems"] });
      
      toast({ title: "Success", description: `Request ${data.status.toLowerCase()} successfully` });
      setUpdated(true);
      onClose(true);
    } catch (error) {
      console.error("Error processing purchase request:", error);
      toast({ 
        title: "Error", 
        description: `Failed to process the request: ${(error as any).message || "Unknown error"}`,
        variant: "destructive"
      });
    }
  };

  // Direct approval handler
  const handleDirectApprove = async () => {
    const approvedBy = form.getValues('approved_by');
    
    if (!approvedBy) {
      toast({ title: "Error", description: "Please select an employee to approve this request", variant: "destructive" });
      return;
    }
    
    try {
      // Update the purchase request status to Approved
      const { error } = await supabase
        .from("purchase_requests")
        .update({
          status: "Approved",
          approved_by: approvedBy,
          approved_at: new Date().toISOString(),
        })
        .eq("id", request.id);
        
      if (error) throw error;
      
      // Refresh purchase requests data
      queryClient.invalidateQueries({ queryKey: ["purchaseRequests"] });
      
      toast({ title: "Success", description: "Request approved successfully" });
      setUpdated(true);
      onClose(true);
    } catch (error) {
      console.error("Error approving purchase request:", error);
      toast({ 
        title: "Error", 
        description: "Failed to approve the request", 
        variant: "destructive" 
      });
    }
  };

  const handleReject = async () => {
    const approvedBy = form.getValues('approved_by');
    
    if (!approvedBy) {
      toast({ title: "Error", description: "Please select an employee to reject this request", variant: "destructive" });
      return;
    }
    
    try {
      const { error } = await supabase
        .from("purchase_requests")
        .update({
          status: "Rejected",
          approved_by: approvedBy,
          approved_at: new Date().toISOString(),
        })
        .eq("id", request.id);
        
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ["purchaseRequests"] });
      toast({ title: "Success", description: "Request rejected" });
      setUpdated(true);
      onClose(true);
    } catch (error) {
      console.error("Error rejecting purchase request:", error);
      toast({ 
        title: "Error", 
        description: "Failed to reject the request", 
        variant: "destructive" 
      });
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "MMM d, yyyy");
  };

  const canApprove = request.status === "Pending";
  const canReject = request.status === "Pending";
  const canPurchase = request.status === "Approved";
  
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(updated)}>
      <DialogContent className="max-w-3xl">
        {!isApproving ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex justify-between items-center">
                <span>Purchase Request</span>
                <Badge 
                  variant="outline"
                  className={getPurchaseRequestStatusColor(request.status)}
                >
                  {request.status}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                {request.description || "No description provided"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Request Details</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Category</p>
                    <p>{request.category}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Estimated Cost</p>
                    <p>{request.estimated_cost ? `${request.currency} ${request.estimated_cost}` : "-"}</p>
                  </div>
                  {request.actual_cost && (
                    <div>
                      <p className="text-sm font-medium">Actual Cost</p>
                      <p>{`${request.currency} ${request.actual_cost}`}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Payment Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Payment Type</p>
                    <p>{request.payment_type || "-"}</p>
                  </div>
                  {request.payment_type === "Recursive" && (
                    <>
                      <div>
                        <p className="text-sm font-medium">Payment Cadence</p>
                        <p>{request.payment_cadence || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Payment Amount</p>
                        <p>
                          {request.payment_amount ? `${request.currency} ${request.payment_amount}` : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Next Payment Date</p>
                        <p>{formatDate(request.next_payment_date)}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Justification</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{request.justification || "No justification provided"}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Request Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Requested By</p>
                    <p>{request.requester_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Created At</p>
                    <p>{formatDate(request.created_at)}</p>
                  </div>
                  {request.approved_by && (
                    <>
                      <div>
                        <p className="text-sm font-medium">Approved/Rejected By</p>
                        <p>{request.approver_name || "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Approval Date</p>
                        <p>{formatDate(request.approved_at)}</p>
                      </div>
                    </>
                  )}
                  {request.inventory_item_id && (
                    <div className="col-span-2">
                      <p className="text-sm font-medium">Linked Inventory Item</p>
                      <p>{request.inventory_item_id}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {(canApprove || canReject) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Approval</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {noEmployeeRecord && (
                      <Alert variant="warning">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Warning</AlertTitle>
                        <AlertDescription>
                          You do not have an employee record. Select an employee to approve on behalf of.
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    <FormField
                      control={form.control}
                      name="approved_by"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Approved By</FormLabel>
                          <Select
                            value={field.value?.toString() || ""}
                            onValueChange={(value) => field.onChange(parseInt(value))}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select an approver" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {employees?.map((employee) => (
                                <SelectItem key={employee.id} value={employee.id.toString()}>
                                  {employee.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
            
            <DialogFooter>
              {canApprove && (
                <Button onClick={handleDirectApprove}>
                  Approve
                </Button>
              )}
              {canReject && (
                <Button variant="outline" onClick={handleReject}>
                  Reject
                </Button>
              )}
              {canPurchase && (
                <Button onClick={() => setIsApproving(true)}>
                  Mark as Purchased
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Mark as Purchased</DialogTitle>
              <DialogDescription>Complete the purchase process</DialogDescription>
            </DialogHeader>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleApprove)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="approved_by"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Approved By</FormLabel>
                      <Select
                        value={field.value?.toString() || ""}
                        onValueChange={(value) => field.onChange(parseInt(value))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an approver" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {employees?.map((employee) => (
                            <SelectItem key={employee.id} value={employee.id.toString()}>
                              {employee.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Purchased">Purchased</SelectItem>
                          <SelectItem value="Adjusted">Adjusted</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="actual_cost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Actual Cost ({request.currency})</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="payment_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select payment type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {paymentTypeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {watchPaymentType === "Recursive" && (
                  <>
                    <FormField
                      control={form.control}
                      name="payment_cadence"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Payment Cadence</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select payment cadence" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {paymentCadenceOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="payment_amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Payment Amount ({request.currency})</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="next_payment_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Next Payment Date</FormLabel>
                          <FormControl>
                            <DatePicker
                              date={field.value}
                              setDate={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                
                {watchStatus === "Purchased" && (
                  <>
                    <FormField
                      control={form.control}
                      name="serial"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Serial Number</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., SN123456789" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="location"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Storage Location</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., Office Cabinet" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsApproving(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {watchStatus === "Purchased"
                      ? "Complete Purchase"
                      : "Update Details"
                    }
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
